import React, { createContext, useContext, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { GraphLinkMode, LayoutSettings, NeighbourSettings } from '../graph/graphViewSettings';
import { defaultLayoutSettings } from '../graph/graphViewSettings';

interface GraphViewSettingsContextProps {
  linkMode: GraphLinkMode;
  setLinkMode: Dispatch<SetStateAction<GraphLinkMode>>;
  hideLogEntries: boolean;
  setHideLogEntries: Dispatch<SetStateAction<boolean>>;
  hideArchivedItems: boolean;
  setHideArchivedItems: Dispatch<SetStateAction<boolean>>;
  layoutSettings: LayoutSettings;
  setLayoutSettings: Dispatch<SetStateAction<LayoutSettings>>;
  showLayoutSettings: boolean;
  setShowLayoutSettings: Dispatch<SetStateAction<boolean>>;
  neighbourSettings: NeighbourSettings;
  setNeighbourSettings: Dispatch<SetStateAction<NeighbourSettings>>;
}

const GraphViewSettingsContext = createContext<GraphViewSettingsContextProps | undefined>(undefined);

export const GraphViewSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [linkMode, setLinkMode] = useState<GraphLinkMode>('explicit-links');
  const [hideLogEntries, setHideLogEntries] = useState<boolean>(true);
  const [hideArchivedItems, setHideArchivedItems] = useState<boolean>(true);
  const [layoutSettings, setLayoutSettings] = useState<LayoutSettings>(defaultLayoutSettings);
  const [showLayoutSettings, setShowLayoutSettings] = useState<boolean>(false);
  const [neighbourSettings, setNeighbourSettings] = useState<NeighbourSettings>('none');

  return (
    <GraphViewSettingsContext.Provider value={{
      linkMode,
      setLinkMode,
      hideLogEntries,
      setHideLogEntries,
      hideArchivedItems,
      setHideArchivedItems,
      layoutSettings,
      setLayoutSettings,
      showLayoutSettings,
      setShowLayoutSettings,
      neighbourSettings,
      setNeighbourSettings
    }}>
      {children}
    </GraphViewSettingsContext.Provider>
  );
};

export const useGraphViewSettingsContext = () => {
  const context = useContext(GraphViewSettingsContext);
  if (!context) {
    throw new Error('useGraphViewSettingsContext must be used within a GraphViewSettingsProvider');
  }
  return context;
};